/**
 * THE OPERATOR METHOD — AI SECURITY DOCTRINE
 *
 * This is the single canonical authority for all security enforcement,
 * acceptable use policy, detection logic, and audit procedures on this platform.
 *
 * Version:  1.0
 * Issued:   March 1, 2026
 * Operator: RAMDESIGNWORKS
 * Platform: theoperatormethod.com
 *
 * All enforcement mechanisms in Cloud Functions, all user-facing legal documents,
 * and all audit log entries reference this doctrine by version number.
 *
 * Statement of authority:
 * "All security enforcement mechanisms in The Operator Method platform derive
 *  from the AI Security Doctrine, Version 1.0, issued March 1, 2026."
 */

export const DOCTRINE_VERSION   = '1.0';
export const DOCTRINE_DATE      = 'March 1, 2026';
export const DOCTRINE_AUTHORITY = 'RAMDESIGNWORKS';
export const DOCTRINE_URL       = '/security-doctrine';

// ─── Section 1: Purpose ───────────────────────────────────────────────────────
export const PURPOSE = {
  headline: 'Why This AI Exists',
  body: [
    'The AI features of The Operator Method exist for one purpose: to help working professionals develop structured, repeatable skill in communicating with and directing AI systems.',
    'Every AI interaction on this platform — the diagnostic chatbot, the course instructor AI, sandbox exercises — is designed to produce measurable improvement in a user\'s operational leverage over AI tools.',
    'The AI is not a general assistant. It is not a search engine. It is not a creative tool for unconstrained use. It is a bounded instructional system operating within a defined educational mission.',
  ],
};

// ─── Section 2: Acceptable Use ───────────────────────────────────────────────
export const ACCEPTABLE_USE = {
  headline: 'What You May Do',
  categories: [
    {
      label: 'Ethical AI Skill Development',
      items: [
        'Practicing prompt construction and refinement',
        'Learning structured AI communication frameworks',
        'Exploring how AI interprets tone, context, and instruction',
        'Developing repeatable workflows for AI-assisted tasks',
      ],
    },
    {
      label: 'Professional Productivity Applications',
      items: [
        'Using AI techniques learned here in your own professional work',
        'Applying course frameworks to business writing, analysis, and communication',
        'Building AI-augmented workflows for legitimate business operations',
        'Testing AI responses for professional use cases covered in the curriculum',
      ],
    },
    {
      label: 'Permissible Exploration',
      items: [
        'Asking AI questions within the scope of the course topic',
        'Challenging AI responses in good faith to test reasoning',
        'Submitting feedback on AI quality, accuracy, or instruction style',
        'Discussing AI capabilities and limitations as they relate to the curriculum',
      ],
    },
  ],
};

// ─── Section 3: Prohibited Use ───────────────────────────────────────────────
export const PROHIBITED_USE = {
  headline: 'What Is Strictly Prohibited',
  categories: [
    {
      label: 'Prompt Injection & System Manipulation',
      items: [
        'Attempting to override, ignore, or circumvent system instructions',
        'Using known jailbreak patterns ("DAN", "unrestricted mode", "ignore previous instructions")',
        'Impersonating the AI system or issuing meta-instructions designed to alter its behavior',
        'Attempting to extract system prompts, configuration, or training information',
      ],
    },
    {
      label: 'Data Harvesting & Privacy Violations',
      items: [
        'Using the AI to extract, aggregate, or scrape personal data of any individual',
        'Attempting to harvest platform data, user records, or session data through AI manipulation',
        'Using the AI to construct phishing content, social engineering scripts, or identity theft aids',
        'Submitting personally identifying information of others without their consent',
      ],
    },
    {
      label: 'Evasion & Identity Obfuscation',
      items: [
        'Using anonymizing tools, proxies, or VPNs to evade session tracking or fingerprinting',
        'Creating multiple accounts to circumvent session limits, bans, or access restrictions',
        'Attempting to spoof browser fingerprints or IP address attribution',
        'Any act designed to conceal the origin or intent of platform activity',
      ],
    },
    {
      label: 'Illegal & Harmful Activity',
      items: [
        'Using the AI for any purpose that violates applicable U.S. federal or state law',
        'Using the AI for any purpose that violates Canadian federal law (PIPEDA, Criminal Code)',
        'Generating content intended to defraud, deceive, defame, threaten, or harass',
        'Using the platform to develop, test, or deploy malicious code, scripts, or exploits',
        'Any use that violates the Computer Fraud and Abuse Act (18 U.S.C. § 1030)',
        'Any use that constitutes cybercrime under the Canadian Cybercrime legislation (C-46)',
      ],
    },
    {
      label: 'Platform Integrity Violations',
      items: [
        'Automated scraping of platform content, lessons, exercises, or AI responses',
        'Reverse-engineering the AI system prompt or instructor behavior',
        'Sharing, redistributing, or commercializing AI-generated course outputs without permission',
        'Interfering with platform infrastructure, API endpoints, or authentication systems',
      ],
    },
  ],
};

// ─── Section 4: Detection Layers ─────────────────────────────────────────────
export const DETECTION_LAYERS = {
  headline: 'How Violations Are Detected',
  layers: [
    {
      name: 'Pattern-Based Injection Detection',
      description: 'Every user message is evaluated against a library of known prompt injection patterns before being processed. This includes jailbreak keywords, instruction override attempts, code injection strings (XSS, SQL), and system interrogation phrases. Detection is server-side and cannot be observed or bypassed by the user.',
    },
    {
      name: 'Intent Classification',
      description: 'The AI model is instructed to identify and refuse off-topic, adversarial, or boundary-testing input. The model\'s own judgment acts as a secondary filter above the pattern library.',
    },
    {
      name: 'Session Escalation Monitoring',
      description: 'Each session\'s exchange count, flagging history, and response patterns are monitored in real time. Repeated boundary-testing within a session escalates the session\'s risk classification automatically.',
    },
    {
      name: 'Browser Fingerprint & IP Binding',
      description: 'Public-facing AI features (the diagnostic chatbot) bind each session to a browser fingerprint derived from IP address and user-agent string. This prevents session reset abuse and ties flagged behavior to a persistent identifier.',
    },
    {
      name: 'Output Filtering',
      description: 'AI responses are not passed to the client before being evaluated for compliance markers. Responses that trigger internal concern flags are suppressed or replaced before delivery.',
    },
    {
      name: 'Authenticated Session Tracking',
      description: 'For logged-in users, all activity is linked to their Firebase UID. Any flagged exchange is recorded against the account and persists across sessions.',
    },
  ],
};

// ─── Section 5: Enforcement Protocol ─────────────────────────────────────────
export const ENFORCEMENT_PROTOCOL = {
  headline: 'How Violations Are Handled',
  levels: [
    {
      level: 'Level 1 — Restricted Response',
      trigger: 'Single detected injection attempt or off-topic misuse',
      action: 'The AI returns a redirection response. The exchange is logged. No user-visible penalty on first occurrence.',
      reversible: true,
    },
    {
      level: 'Level 2 — Session Suspension',
      trigger: 'Multiple flagged exchanges within a single session, or a single high-severity pattern match',
      action: 'The session is terminated. The user may be redirected to the Purgatory page. The fingerprint is logged as a risk indicator.',
      reversible: true,
    },
    {
      level: 'Level 3 — Account Ban',
      trigger: 'Confirmed malicious input, repeated session abuse, or a pattern consistent with automated attack',
      action: 'The account UID and browser fingerprint are permanently banned. All AI access is revoked. The ban is recorded in the security log with full event detail.',
      reversible: false,
    },
    {
      level: 'Level 4 — Legal Referral',
      trigger: 'Evidence of criminal activity, targeted harassment, data theft attempts, or coordinated abuse',
      action: 'All relevant logs, session data, IP records, and fingerprints are preserved and may be disclosed to law enforcement or legal counsel. The user\'s agreement to the Terms of Service constitutes consent to this disclosure.',
      reversible: false,
    },
  ],
};

// ─── Section 6: Logging & Audit ───────────────────────────────────────────────
export const LOGGING_AUDIT = {
  headline: 'What We Log and How Long We Keep It',
  logTypes: [
    {
      type: 'Security Event Logs',
      collection: 'security_logs',
      content: 'Event type, user UID or fingerprint, IP address, timestamp, input snippet (first 500 chars)',
      retention: 'Indefinite — security logs are never auto-deleted. Manual review required before any deletion.',
    },
    {
      type: 'Demo Session Fingerprints',
      collection: 'demo_fingerprints',
      content: 'SHA-256 fingerprint of IP + user-agent, canonical session ID, completion status, ban flag',
      retention: '24 months from last activity, then purged unless flagged.',
    },
    {
      type: 'Chat Session Transcripts',
      collection: 'chat_sessions',
      content: 'Full conversation history (last 20 exchanges), lesson count, assessed level, session timestamps',
      retention: 'Duration of account activity + 24 months. Deleted on account deletion request.',
    },
    {
      type: 'Beta Activity Logs',
      collection: 'beta_activity_logs',
      content: 'User UID, session ID, exchange index, lesson count, timestamp',
      retention: '24 months from beta program end date.',
    },
    {
      type: 'User Account Records',
      collection: 'users',
      content: 'Email, name, tier, progress, ban status, beta flags, TOS acceptance timestamps',
      retention: 'Duration of account + 24 months. Deleted within 30 days of verified deletion request.',
    },
    {
      type: 'Feedback Submissions',
      collection: 'feedback',
      content: 'Survey responses (Q1–Q6), module ID, user ID, submission timestamp',
      retention: 'Indefinite for aggregated analysis. Individual records deleted on account deletion request.',
    },
  ],
  reviewProcedure: 'Security logs are reviewed by authorized RAMDESIGNWORKS administrators on demand. Flagged accounts are reviewed within 72 hours of ban issuance. All log access requires Firebase admin credentials.',
  auditTrail: 'Every security enforcement action includes a doctrine_version field in the log entry identifying the version of this doctrine in effect at the time of the event.',
};

// ─── Section 7: Compliance Philosophy ────────────────────────────────────────
export const COMPLIANCE_PHILOSOPHY = {
  headline: 'Our Approach to Compliance',
  statements: [
    'We build enforcement first. No feature is shipped without corresponding security controls and logging.',
    'We document what we do. This doctrine is not aspirational — it describes systems that are already implemented and operational.',
    'We version our doctrine. Every change to security logic is reflected in a new doctrine version, ensuring full traceability between the legal record and the deployed code.',
    'We comply with applicable law. Our platform serves users in the United States and Canada. We comply with U.S. federal law (CFAA, COPPA, CAN-SPAM, ECPA), applicable state laws (Ohio, CCPA for California residents), and Canadian federal law (PIPEDA, Anti-Spam Legislation CASL).',
    'We do not surveil for commercial gain. All monitoring and logging exists exclusively for platform security and product development. No data is sold, licensed, or shared with advertisers.',
    'We enforce proportionately. Enforcement escalates in proportion to the severity and persistence of the violation. First-time technical misuse is handled quietly. Coordinated abuse is handled legally.',
  ],
};

// ─── Consolidated export ──────────────────────────────────────────────────────
const SECURITY_DOCTRINE = {
  version:             DOCTRINE_VERSION,
  date:                DOCTRINE_DATE,
  authority:           DOCTRINE_AUTHORITY,
  url:                 DOCTRINE_URL,
  purpose:             PURPOSE,
  acceptableUse:       ACCEPTABLE_USE,
  prohibitedUse:       PROHIBITED_USE,
  detectionLayers:     DETECTION_LAYERS,
  enforcementProtocol: ENFORCEMENT_PROTOCOL,
  loggingAudit:        LOGGING_AUDIT,
  compliancePhilosophy: COMPLIANCE_PHILOSOPHY,
};

export default SECURITY_DOCTRINE;
